import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { AutoTooltip, Button } from "nlib/ui";
import { checkIsBusinessUser } from "selectors/user";
import { getSelectedBusinessCategories, getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getTransactionsReasons } from "selectors/transactions";
import { useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import DataStatuses from "const/DataStatuses";
import IntegrationServices from "const/IntegrationServices";
import React, { useCallback, useMemo } from "react";
import Texts from "utils/Texts";
import classNames from "classnames";

const AdditionalInfo = (props) => {
  const {
    disabled,
    auditRoute,
    usesItems,
    status,
    documentId,
    initialReason,
    category: {
      code: categoryCode = "",
      setByClient: categorySetByClient = false,
      setByRule: categorySetByRule = false,
      confidence: categoryConfidence = 0
    } = {},
    item: {
      setByClient: itemSetByClient = false
    } = {},
    disabledCategorySelect,
    onCategoryChange,
    onCategorizedByClick
  } = props;

  const { uiTexts, messages } = useSelector(getTextsData);

  const businessUser = useSelector(checkIsBusinessUser);

  const transactionsReasons = useSelector(getTransactionsReasons);

  const categories = useSelector(getSelectedBusinessCategories);

  const {
    extraData: { integrationService, uncategorizedRpaEnabled } = {}
  } = useSelector(getSelectedBusinessData);

  const rpaMode = uncategorizedRpaEnabled !== undefined;

  const suggestedCategory = useMemo(() => {
    const suggestedCategoryCode = initialReason
      && Arrays.findByKey(transactionsReasons, "reason", initialReason, {}).categoryCode;

    return suggestedCategoryCode && Arrays.findByKey(categories, "code", suggestedCategoryCode);
  }, [categories, initialReason, transactionsReasons]);

  const handleCategoryLinkClick = useCallback(() => {
    onCategoryChange(suggestedCategory);
  }, [suggestedCategory, onCategoryChange]);

  if (auditRoute) return null;
  if (
    suggestedCategory
    && !usesItems
    && !categoryCode
    && !businessUser
    && status === DataStatuses.TO_REVIEW
    && !disabledCategorySelect
  ) {
    const [categorySuggestionTextA, categorySuggestionTextB] = messages.categorySuggestion;

    return (
      <div className={Css.additionalInfo}>
        <AutoTooltip className={Css.description} title={uiTexts.categorizationSuggestion}>
          <Icons.SquaresFour className={Css.highlight} />
          <span>{`${categorySuggestionTextA}:`}</span>
          <a onClick={handleCategoryLinkClick} disabled={disabled}>
            {`${suggestedCategory?.displayName}.`}
          </a>
          <span>{categorySuggestionTextB}</span>
          <span>{`«${initialReason}»`}</span>
        </AutoTooltip>
      </div>
    );
  }
  if (initialReason && (status === DataStatuses.TO_REVIEW || status === DataStatuses.NEED_REACTION)) {
    const aiReason = initialReason.startsWith("AI: ");

    return (
      <div className={Css.additionalInfo}>
        <AutoTooltip className={Css.description} title={aiReason ? uiTexts.aiComment : uiTexts.userResponse}>
          {aiReason
            ? <Icons.Cpu className={Css.highlight} />
            : <Icons.Chat className={Css.highlight} />}
          <span>{initialReason}</span>
        </AutoTooltip>
      </div>
    );
  }
  if ((usesItems ? itemSetByClient : categorySetByClient) && status === DataStatuses.TO_REVIEW) {
    return (
      <div className={Css.additionalInfo} title={uiTexts.categorizedByClient}>
        <AutoTooltip className={Css.description}>
          <Icons.User className={Css.highlight} />
          <span>{uiTexts.categorizedByClient}</span>
        </AutoTooltip>
      </div>
    );
  }
  if (!usesItems && categorySetByRule && status === DataStatuses.TO_REVIEW) {
    const integrationServiceName = IntegrationServices.getByValue(integrationService).shortLabel;

    const value = Texts.replaceVars(uiTexts.categorizedByServiceRule, { service: integrationServiceName });

    return (
      <div className={Css.additionalInfo}>
        <AutoTooltip className={Css.description} title={value}>
          <Icons.SquaresFour className={Css.highlight} />
          <span>{Texts.replaceVars(uiTexts.categorizedByServiceRule, { service: integrationServiceName })}</span>
        </AutoTooltip>
      </div>
    );
  }
  if (documentId && status === DataStatuses.TO_REVIEW) {
    return (
      <div className={Css.additionalInfo}>
        <AutoTooltip className={Css.description} title={uiTexts.documentMatched}>
          <Icons.FileText className={Css.highlight} />
          <span>{rpaMode ? uiTexts.documentMatched : uiTexts.documentAttached}</span>
        </AutoTooltip>
      </div>
    );
  }
  if (!usesItems && categoryConfidence) {
    if (rpaMode) {
      return (
        <div className={classNames(Css.additionalInfo, Css.button)}>
          <Button
            outline
            className={Css.description}
            icon={Icons.Cpu}
            onClick={onCategorizedByClick}>
            <span className={Css.ellipsis}>{uiTexts.categorizedByAiBookkeeper}</span>
            <Icons.CaretRight weight="bold" className={Css.caret} />
          </Button>
        </div>
      );
    }

    return (
      <div className={Css.additionalInfo}>
        <AutoTooltip className={Css.description} title={uiTexts.categorizedAutomatically}>
          <Icons.Cpu className={Css.highlight} />
          <span>{uiTexts.categorizedAutomatically}</span>
        </AutoTooltip>
      </div>
    );
  }

  return null;
};

export default React.memo(AdditionalInfo);
